import React, { useState, useEffect, useCallback, useRef } from "react";
import { Table, Form, Spin, message, Divider } from "antd";
import {
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import styled from "@emotion/styled";
import ClaimsLogModal from "../organisms/ClaimsLogModal";
import { ReactComponent as Editicon } from "../assets/Edit_Pencil_01.svg";
import { ReactComponent as TrashDelete } from "../assets/Trash_Full.svg";
import { ReactComponent as FilterIcon } from "../assets/filter-svgrepo-com (2).svg";
import { DropdownCheckBox } from "atoms/UIDropdown/Dropdown";
import { ButtonFilled } from "reusableComponent/UIButtons";
import dayjs from "dayjs";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import COLORS from "common/Colors";
import { currencySymbolMap } from "common/Constants";
import ClaimsFilterModal from "./ClaimsFilterModal";

/* ─────────────── Styled Components ─────────────── */

const Wrapper = styled.div`
  background: #fff;
  padding: 0;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const TotalLog = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const FiltersRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterBtn = styled.div`
  position: relative;
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: #C2FAE5;
  cursor: pointer;
  color: #6b7280;
  flex-shrink: 0;
  transition: border-color 0.2s;
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #fff;
`;

const DropdownWrapper = styled.div`
  width: 150px;
  .custom-filter-btn {
    width: 100% !important;
    margin-bottom: 0 !important;
  }
`;

const StyledTable = styled(Table)`
  padding-top: 10px;
  table {
    width: 100% !important;
    border: 1px solid #ccc;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    color: ${COLORS.TEXT.PRIMARY};
    font-size: 14px;
  }
  .ant-table-row:nth-child(even) {
    background-color: #fafafa;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: bold;
    color: ${COLORS.TEXT.PRIMARY};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
  .ant-select-selector {
    min-width: 150px;
  }
  .matter-main {
    font-weight: 500;
    color: #111827;
    display: block;
    line-height: 1.4;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .matter-sub {
    font-size: 12px;
    color: #9ca3af;
    display: block;
    margin-top: 1px;
  }
  .status-billed,
  .status-unbilled,
  .status-pending,
  .status-rejected {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }
  .ant-table-tbody > tr .action-icons {
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  .ant-table-tbody > tr:hover .action-icons {
    opacity: 1;
  }
`;

const IconBtn = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s;
`;

/* ─────────────── Detail Modal Styled ─────────────── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease;
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 560px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  animation: slideUp 0.2s ease;
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 24px 14px;
`;

const ModalTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
`;

const CreatedBadge = styled.span`
  font-size: 12px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 6px;
  padding: 3px 10px;
  font-weight: 500;
  strong {
    color: #374151;
    font-weight: 600;
  }
`;

const ModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.15s;
  &:hover { background: #f3f4f6; color: #374151; border-radius: 6px; }
`;

const ModalBody = styled.div`
  padding: 4px 24px 24px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;

const DetailLabel = styled.span`
  width: 148px;
  flex-shrink: 0;
  font-size: 14px;
  color: #888;
  font-weight: 500;
  padding-top: 1px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 450;
  line-height: 1.6;
`;

const AttachmentLink = styled.a`
  font-size: 14px;
  color: #0d9488;
  font-weight: 500;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const DescriptionSection = styled.div`
  padding: 14px 0 4px;
`;

const DescriptionLabel = styled.div`
  font-size: 14px;
  color: #888;
  font-weight: 500;
  margin-bottom: 6px;
`;

const DescriptionText = styled.p`
  font-size: 14px;
  color: #111827;
  line-height: 1.75;
  margin: 0;
  font-weight: 450;
`;

const BillItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: #f0fdf9; }
  .bill-icon { font-size: 16px; flex-shrink: 0; }
  .bill-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #0d9488;
    font-weight: 500;
    font-size: 13px;
  }
`;

/* ─────────────── Detail Modal Component ─────────────── */

function ClaimsDetailModal({ record, onClose, onEdit, onDelete }) {
  if (!record) return null;

  console.log("detail record attachment:", record.attachment);

  const isUnbilled = record.status?.toLowerCase() === "unbilled";

  return (
    <Overlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitleBlock>
            <ModalTitle>Claims Log</ModalTitle>
            <CreatedBadge>
              Created on &nbsp;<strong>{record.createdat ? dayjs(record.createdat).format("Do MMM YYYY") : "—"}</strong>
            </CreatedBadge>
          </ModalTitleBlock>

          <ModalActions style={{ gap: 15 }}>
            {isUnbilled && (
              <>
                <EditOutlined
                  onClick={(e) => { e.stopPropagation(); onClose(); onEdit(record); }}
                  style={{
                    color: "#00685F",
                    cursor: "pointer",
                    background: "#deefee",
                    padding: "2px",
                    borderRadius: "4px",
                    height: "25px",
                    width: "25px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18
                  }}
                />
                <TrashDelete
                  onClick={(e) => { e.stopPropagation(); onDelete(record); onClose(); }}
                  style={{
                    color: "#00685F",
                    cursor: "pointer",
                    background: "#deefee",
                    padding: "2px",
                    borderRadius: "4px",
                    height: "25px",
                    width: "25px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18
                  }}
                />
              </>
            )}
            <CloseBtn onClick={onClose}><CloseOutlined /></CloseBtn>
          </ModalActions>
        </ModalHeader>

        <ModalBody>
          {[
            ["Client", record.clientname || record.client],
            ["Matter", record.mattername || record.matter],
            ["Date of Log", record.dateoflog || record.dateOfLog ? dayjs(record.dateoflog || record.dateOfLog).format("Do MMM YYYY") : "—"],
            ["Tax Type", record.taxtype || record.taxType],
            ["Type", record.type],
            ["Total Amount", (() => { const symbol = currencySymbolMap[record.currency]; const amount = record.totalamount ?? record.totalAmount; return amount != null ? `${symbol} ${amount}` : "—"; })()],
          ].map(([label, value]) => (
            <DetailRow key={label}>
              <DetailLabel>{label}</DetailLabel>
              <DetailValue>{value || "—"}</DetailValue>
            </DetailRow>
          ))}

          {Array.isArray(record.attachment) && record.attachment.filter(a => a?.url || a?.name || a?.objectKey || a?.fileName).length > 0 && (
            <DetailRow>
              <DetailLabel>Attachment</DetailLabel>
              <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                {record.attachment.filter(a => a?.url || a?.objectKey || a?.name || a?.fileName).map((a, i) => (
                  <BillItem
                    key={i}
                    onClick={async () => {
                      if (a.url && !a.objectKey) {
                        window.open(a.url, "_blank");
                        return;
                      }
                      if (a.objectKey) {
                        try {
                          const res = await CALL_API(
                            "insyts/timesheet/claims?preview",
                            "post",
                            { objectKey: a.objectKey }
                          );
                          const url = res?.response?.url ?? res?.url;
                          if (url) window.open(url, "_blank");
                          else message.error("Preview URL not available");
                        } catch {
                          message.error("Failed to load file");
                        }
                        return;
                      }
                      message.error("No file available");
                    }}
                  >
                    <span className="bill-icon">📄</span>
                    <span className="bill-name">{a.fileName || a.name || a.url || a}</span>
                  </BillItem>
                ))}
              </div>
            </DetailRow>
          )}
          {typeof record.attachment === "string" && record.attachment && (
            <DetailRow>
              <DetailLabel>Attachment</DetailLabel>
              <BillItem onClick={() => window.open(record.attachment, "_blank")}>
                <span className="bill-icon">📄</span>
                <span className="bill-name">{record.attachment}</span>
              </BillItem>
            </DetailRow>
          )}

          <DescriptionSection>
            <DescriptionLabel>Log Description</DescriptionLabel>
            <DescriptionText>{record.description || "—"}</DescriptionText>
          </DescriptionSection>
        </ModalBody>
      </ModalBox>
    </Overlay>
  );
}

/* ─────────────── Main Component ─────────────── */

export default function ClaimsTab({ matterId }) {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;

  const [filterOptions, setFilterOptions] = useState({
    taxTypes: ["VAT", "Non-VAT"],
    matters: [],
    clients: [],
    statuses: ["Billed", "Unbilled"],
  });


  // On global view (no matterId prop), load all distinct matters once for the filter dropdown
  useEffect(() => {
    if (matterId) return;
    (async () => {
      try {
        const res = await CALL_FEATCH_API(`insyts/timesheet/claims?page=1&limit=1000`, "get");
        const rows = res?.response?.data ?? res?.data ?? [];
        const seen = new Map();
        rows.forEach((r) => {
          const id = r.matterid || r.matterId;
          const name = r.mattername || r.matter;
          if (id && !seen.has(id)) seen.set(id, name || id);
        });
        const matters = [...seen.entries()]
          .map(([id, name]) => ({ label: name, value: id }))
          .sort((a, b) => a.label.localeCompare(b.label));
        if (matters.length) setFilterOptions((prev) => ({ ...prev, matters }));
      } catch { }
    })();
  }, []);

  // On global view, load all distinct clients once for the filter dropdown
  useEffect(() => {
    if (matterId) return;
    (async () => {
      try {
        const res = await CALL_FEATCH_API(`insyts/timesheet/claims?page=1&limit=1000`, "get");
        const rows = res?.response?.data ?? res?.data ?? [];
        const uniqueClients = [
          ...new Set(rows.map((r) => r.clientname || r.client).filter(Boolean)),
        ].sort();
        if (uniqueClients.length) setFilterOptions((prev) => ({ ...prev, clients: uniqueClients }));
      } catch { }
    })();
  }, []);

  // Load distinct taxtype options for this matter from the filter-list API
  useEffect(() => {
    if (!matterId) return;
    (async () => {
      try {
        const res = await CALL_FEATCH_API(
          `insyts/timesheet/claims-filterlist?matterid=${matterId}`,
          "get"
        );
        // backend now returns data as a flat array of strings e.g. ["VAT", "Non-VAT"]
        const taxTypes = res?.response?.data ?? res?.data ?? [];
        if (Array.isArray(taxTypes) && taxTypes.length) {
          setFilterOptions((prev) => ({ ...prev, taxTypes }));
        }
      } catch {
        // fall back to defaults already in state
      }
    })();
  }, [matterId]);

  const [filters, setFilters] = useState({
    taxType: null,
    matter: null,
    client: null,
    status: null,
    currency: null,
    type: null,
    logDateFrom: null,
    logDateTo: null,
    createdDateFrom: null,
    createdDateTo: null,
  });

  const [detailRecord, setDetailRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [form] = Form.useForm();
  const containerRef = useRef(null);

  /* ── Fetch ── */
  const fetchClaims = useCallback(async () => {
    setFetching(true);
    try {

      const { taxType, matter, client, status, currency, logDateFrom, logDateTo, createdDateFrom, createdDateTo } = filters;

      const appendMulti = (url, key, val) => {
        if (!val) return url;
        const arr = Array.isArray(val) ? val : [val];
        arr.forEach((v) => { url += `&${key}=${encodeURIComponent(v)}`; });
        return url;
      };

      let url = `insyts/timesheet/claims?page=${page}&limit=${limit}`;
      if (matterId) url += `&matterid=${matterId}`;
      else url = appendMulti(url, "matterid", matter);
      url = appendMulti(url, "taxtype", taxType);
      url = appendMulti(url, "clientname", client);
      url = appendMulti(url, "status", status);
      url = appendMulti(url, "currency", currency);
      url = appendMulti(url, "type", filters.type);
      if (logDateFrom) url += `&logfrom=${dayjs(logDateFrom).format("YYYY-MM-DD")}`;
      if (logDateTo) url += `&logto=${dayjs(logDateTo).format("YYYY-MM-DD")}`;
      if (createdDateFrom) url += `&createdfrom=${dayjs(createdDateFrom).startOf("day").format("YYYY-MM-DDTHH:mm:ss")}`;
      if (createdDateTo) url += `&createdto=${dayjs(createdDateTo).endOf("day").format("YYYY-MM-DDTHH:mm:ss")}`;

      const res = await CALL_FEATCH_API(url, "get");

      console.log("Claims API response:", res); // 👈 check this in console

      const responseData = res?.response?.data ?? res?.data ?? [];
      const responseTotal =
        res?.response?.pagination?.total ??  // { response: { pagination: { total } } }
        res?.pagination?.total ??            // { pagination: { total } }
        res?.response?.total ??              // { response: { total } }
        res?.total ??                        // { total }
        responseData.length;                 // fallback

      setData(responseData);
      setTotal(responseTotal);

    } catch {
      message.error("Failed to load claims.");
    } finally {
      setFetching(false);
    }
  }, [matterId, filters, page]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  /* ── Helpers ── */
  const setFilter = (key, value) => {
    setPage(1);
    const normalized = Array.isArray(value) ? (value.length ? value : null) : (value || null);
    setFilters((prev) => ({ ...prev, [key]: normalized }));
  };

  const syncFilters = () => {
    const vals = form.getFieldsValue();
    setFilter("matter", vals.matter?.length ? vals.matter : null);
    setFilter("taxType", vals.taxType?.length ? vals.taxType : null);
    setFilter("client", vals.client?.length ? vals.client : null);
    setFilter("status", vals.status?.length ? vals.status : null);
  };

  const clearAllFilters = () => {
    form.resetFields();
    setFilters({
      taxType: null,
      matter: null,
      client: null,
      status: null,
      currency: null,
      type: null,
      logDateFrom: null,
      logDateTo: null,
      createdDateFrom: null,
      createdDateTo: null,
    });
    setPage(1);
  };

  const activeFilterCount = Object.values(filters).filter((v) => Array.isArray(v) ? v.length > 0 : Boolean(v)).length;

  /* ── Delete ── */
  const handleDelete = async (record, e) => {
    e?.stopPropagation();
    try {
      await CALL_API("insyts/timesheet/claims", "delete", {
        claimsid: record.claimsid,
        deletedby: record.userid || "",
      });
      message.success("Deleted successfully");
      fetchClaims();
      if (detailRecord?.claimsid === record.claimsid) setDetailRecord(null);
    } catch {
      message.error("Delete failed");
    }
  };

  /* ── Open modals ── */
  const openCreate = () => {
    setEditRecord(null);
    setModalVisible(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    setModalVisible(true);
  };

  /* ── Status tag ── */
  const getStatusTag = (status = "") => {
    const s = status.toLowerCase();
    if (s === "billed") return <span className="status-billed">{status}</span>;
    if (s === "unbilled") return <span className="status-unbilled">{status}</span>;
    if (s === "pending") return <span className="status-pending">{status}</span>;
    if (s === "rejected") return <span className="status-rejected">{status}</span>;
    return <span className="status-unbilled">{status}</span>;
  };

  /* ── Columns ── */
  const columns = [
    {
      title: "Matter",
      key: "matter",
      render: (_, r) => (
        <>
          <span className="matter-main">{r.mattername || r.matter || "—"}</span>
          <span className="matter-sub">{r.matterid || r.matterId || ""}</span>
        </>
      ),
    },
    {
      title: "Client",
      key: "client",
      render: (_, r) => r.clientname || r.client || "—",
    },
    {
      title: "Date of Log",
      key: "dateoflog",
      render: (_, r) => {
        const date = r.dateoflog || r.dateOfLog;
        return date ? dayjs(date).format("Do MMM YYYY") : "—";
      },
    },
    {
      title: "Tax Type",
      key: "taxtype",
      render: (_, r) => r.taxtype || r.taxType || "—",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (v) => v || "—",
    },
    {
      title: "Total Amount",
      key: "totalamount",
      render: (_, r) => {
        const symbol = currencySymbolMap[r.currency] ?? r.currency ?? "";
        const amount = r.totalamount ?? r.totalAmount;
        return amount != null ? `${symbol} ${amount}` : "—";
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => getStatusTag(r.status),
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, record) => {
        if (record.status?.toLowerCase() !== "unbilled") return null;
        return (
          <div
            className="action-icons"
            style={{ display: "flex", gap: 25, alignItems: "center", justifyContent: "flex-end" }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconBtn onClick={(e) => { e.stopPropagation(); openEdit(record); }}>
              <EditOutlined style={{
                color: "#00685F",
                cursor: "pointer",
                background: "#deefee",
                padding: "2px",
                borderRadius: "4px",
                height: "25px",
                width: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18
              }} />
            </IconBtn>
            <TrashDelete
              onClick={(e) => handleDelete(record, e)}
              style={{
                cursor: "pointer", color: "#00685F",
                background: "#deefee",
                padding: "2px",
                borderRadius: "4px",
                height: "25px",
                width: "25px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <Wrapper ref={containerRef}>
      <Toolbar>
        <TotalLog>Total Log: {total || data.length}</TotalLog>

        <Form form={form} layout="inline" onValuesChange={syncFilters}>
          <FiltersRight>
            {!matterId && (
              <DropdownWrapper>
                <DropdownCheckBox
                  name="matter"
                  label="Matter"
                  options={filterOptions.matters}
                  form={form}
                  handleFormChange={syncFilters}
                />
              </DropdownWrapper>
            )}

            <DropdownWrapper>
              <DropdownCheckBox
                name="client"
                label="Client"
                options={filterOptions.clients.map((c) => ({ label: c, value: c }))}
                form={form}
                handleFormChange={syncFilters}
              />
            </DropdownWrapper>

            <DropdownWrapper>
              <DropdownCheckBox
                name="status"
                label="Status"
                options={filterOptions.statuses.map((s) => ({ label: s, value: s }))}
                form={form}
                handleFormChange={syncFilters}
              />
            </DropdownWrapper>

            <div style={{ display: "flex", alignItems: "center" }}>
              <ClaimsFilterModal
                onApply={(f) => {
                  setPage(1);
                  setFilters((prev) => ({
                    ...prev,
                    currency: f.currency || null,
                    taxType: Array.isArray(f.taxtype) ? (f.taxtype.length ? f.taxtype : null) : (f.taxtype || null),
                    type: f.type || null,
                    logDateFrom: f.logDateFrom || null,
                    logDateTo: f.logDateTo || null,
                    createdDateFrom: f.createdDateFrom || null,
                    createdDateTo: f.createdDateTo || null,
                  }));
                }}
                onClear={() => {
                  setPage(1);
                  setFilters({ taxType: null, client: null, status: null, currency: null, type: null, logDateFrom: null, logDateTo: null, createdDateFrom: null, createdDateTo: null });
                }}
              />
            </div>

            <Divider type="vertical" style={{ height: 24, margin: "0 12px" }} />

            <ButtonFilled onClick={openCreate}>
              Create Claims Log
            </ButtonFilled>
          </FiltersRight>

        </Form>
      </Toolbar>

      <Spin spinning={fetching}>
        <StyledTable
          dataSource={data}
          columns={columns}
          rowKey={(r, i) => r.id || `row-${i}`}
          pagination={{
            current: page,
            pageSize: limit,
            total: total,
            showSizeChanger: false,
            showTotal: (t) => `Total ${t} records`,
            onChange: (p) => setPage(p),
            style: { padding: "12px 20px" },
          }}
          onRow={(record) => ({
            onClick: () => setDetailRecord(record),
            style: { cursor: "pointer" },
          })}
          locale={{ emptyText: "No claims logs found." }}
        />
      </Spin>

      {/* Detail Modal */}
      {detailRecord && (
        <ClaimsDetailModal
          record={detailRecord}
          onClose={() => setDetailRecord(null)}
          onEdit={(rec) => { setDetailRecord(null); openEdit(rec); }}
          onDelete={(rec) => handleDelete(rec)}
        />
      )}

      {/* Create / Edit Modal */}
      <ClaimsLogModal
        visible={modalVisible || !!editRecord}
        onClose={() => { setModalVisible(false); setEditRecord(null); }}
        editRecord={editRecord}
        claimId={editRecord?.claimsid}
        matterId={matterId}
        onSuccess={() => {
          setModalVisible(false);
          setEditRecord(null);
          fetchClaims();
        }}
      />
    </Wrapper>
  );
}